import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useCart } from "@/contexts/CartContext";
import { supabase } from "@/lib/supabase";
import CheckoutForm, { type CheckoutFormValues } from "./components/CheckoutForm";
import OrderSummary from "./components/OrderSummary";

const SHIPPING_FREE_THRESHOLD = 95;
const SHIPPING_FLAT = 8.5;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const shipping = useMemo(
    () => (subtotal >= SHIPPING_FREE_THRESHOLD || subtotal === 0 ? 0 : SHIPPING_FLAT),
    [subtotal],
  );
  const total = useMemo(() => subtotal + shipping, [subtotal, shipping]);

  const handlePlaceOrder = async (values: CheckoutFormValues) => {
    if (items.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const fullName = `${values.firstName} ${values.lastName}`.trim();

      const recipient: Record<string, string> = {};
      if (fullName) recipient.name = fullName;
      if (values.email) recipient.email = values.email;
      if (values.country) recipient.country = values.country;
      if (values.state) recipient.state = values.state;
      if (values.city) recipient.city = values.city;
      if (values.zip) recipient.zip = values.zip;
      if (values.address) recipient.address = values.address;

      const { data: sessionData } = await supabase.auth.getSession();
      const customerId = sessionData?.session?.user?.id ?? null;

      const { data: orderData, error: orderErr } = await supabase
        .from("order_headers")
        .insert({
          customer_id: customerId,
          status: "pending_payment",
          currency: "USD",
          payment_provider: "manual",
          subtotal_items: subtotal,
          shipping_total: shipping,
          recipient,
        })
        .select("id")
        .single();

      if (orderErr) throw orderErr;
      if (!orderData?.id) throw new Error("Order was not created");

      const itemRows = items.map((it) => ({
        order_id: orderData.id,
        product_id: String(it.productId),
        product_name: it.productName,
        sku_id: it.skuId ? String(it.skuId) : null,
        sku_label: it.skuLabel,
        quantity: it.quantity,
        unit_price: it.unitPrice,
        final_price: it.finalPrice,
        subtotal: it.finalPrice * it.quantity,
      }));

      const { error: itemsErr } = await supabase
        .from("order_items")
        .insert(itemRows);

      if (itemsErr) throw itemsErr;

      clear();
      navigate(`/checkout/success/${orderData.id}`);
    } catch (e) {
      setSubmitError(
        e instanceof Error ? e.message : "We couldn't place your order. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background-50 text-foreground-950">
      <Navbar />

      <section className="pt-28 md:pt-32 pb-10 px-4 md:px-10 lg:px-14">
        <div className="max-w-5xl mx-auto">
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">
            Checkout
          </h1>
        </div>
      </section>

      <section className="px-4 md:px-10 lg:px-14 pb-20 md:pb-28">
        <div className="max-w-5xl mx-auto">
          {items.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 lg:gap-12">
              <div>
                {submitError && (
                  <div className="mb-5 rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm text-primary-900">
                    {submitError}
                  </div>
                )}
                <CheckoutForm onSubmit={handlePlaceOrder} submitting={submitting} />
              </div>

              <div>
                <OrderSummary
                  items={items}
                  subtotal={subtotal}
                  shipping={shipping}
                  total={total}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}

function EmptyState() {
  return (
    <div className="max-w-xl mx-auto text-center py-12">
      <p className="text-sm text-foreground-700">Your cart is empty.</p>
      <Link
        to="/#collections"
        className="mt-5 inline-flex items-center gap-2 px-5 h-10 rounded-full bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-900 cursor-pointer whitespace-nowrap"
      >
        Continue shopping
      </Link>
    </div>
  );
}